import { HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

interface CacheEntry {
    response: HttpResponse<unknown>;
    timestamp: number;
}

/**
 * Process-global in-memory cache for HTTP GET responses, used ONLY during SSR
 * (`SsrCacheBackend` is a passthrough in the browser). The `static` members are
 * intentional, not a leak: the Express/Node process is long-lived and serves
 * every visitor, so one shared cache lets concurrent server renders reuse a
 * single backend response instead of each refetching.
 *
 * - Entries expire after 5 minutes and the map is bounded to MAX_ENTRIES
 *   (oldest-inserted evicted first) so it cannot grow unbounded.
 * - `inFlight` coalesces concurrent identical requests: while one backend call
 *   is in progress, other renders for the same key share its response instead
 *   of issuing a duplicate request — this is what defuses the cache-stampede
 *   when many users hit the same cold URL at the same time.
 *
 * Keys are built by the interceptor and include the Accept-Language header, so
 * a Polish render is never served to a German visitor.
 */
@Injectable({
    providedIn: 'root',
})
export class CacheService {
    private static readonly requestCache = new Map<string, CacheEntry>();
    private static readonly inFlight = new Map<string, Observable<HttpEvent<unknown>>>();
    private static readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds
    private static readonly MAX_ENTRIES = 200;

    get(key: string): HttpResponse<unknown> | undefined {
        const entry = CacheService.requestCache.get(key);

        if (!entry) {
            return undefined;
        }

        const isExpired = Date.now() - entry.timestamp > CacheService.CACHE_DURATION_MS;

        if (isExpired) {
            CacheService.requestCache.delete(key);
            return undefined;
        }

        return entry.response;
    }

    set(key: string, response: HttpResponse<unknown>): void {
        const cache = CacheService.requestCache;

        // Map preserves insertion order, so the first key is the oldest one.
        if (cache.size >= CacheService.MAX_ENTRIES && !cache.has(key)) {
            // size >= MAX_ENTRIES ⇒ the map is non-empty, so the first key exists.
            cache.delete(<string>cache.keys().next().value);
        }

        cache.set(key, { response, timestamp: Date.now() });
    }

    getInFlight(key: string): Observable<HttpEvent<unknown>> | undefined {
        return CacheService.inFlight.get(key);
    }

    setInFlight(key: string, request$: Observable<HttpEvent<unknown>>): void {
        CacheService.inFlight.set(key, request$);
    }

    clearInFlight(key: string): void {
        CacheService.inFlight.delete(key);
    }
}
